// components/Events/StatChangeEvent.tsx

import React, { useContext } from 'react';
import { BaseRendererProps } from './eventRenderers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTurnUp } from '@fortawesome/free-solid-svg-icons';
 import './StatChangeEvent.css';
import GameAssetsContext from '../../../../../../context/GameAssetContext';
import { itemNameToId } from '../../../../../../mappings/itemMappings';
import { championImages } from './Images/championImages';
import debuffImages from './Images/debuffImages';
import { getPetImageUrl } from './Images/petImages';

// Helper function to convert rune display names to camelCase keys
function convertRuneNameToKey(runeName: string): string {
  // Create a mapping for special cases and common rune names
  const runeNameMapping: Record<string, string> = {
    'Axiom Arcanist': 'axiomArcanist',
    'Phase Rush': 'phaseRush',
    'Summon Aery': 'summonAery',
    'Arcane Comet': 'arcaneComet',
    'Manaflow Band': 'manaflowBand',
    'Nimbus Cloak': 'nimbusCloak',
    'Absolute Focus': 'absoluteFocus',
    'Gathering Storm': 'gatheringStorm',
    'Water Walking': 'waterWalking',
    'Press the Attack': 'pressTheAttack',
    'Lethal Tempo': 'lethalTempo',
    'Fleet Footwork': 'fleetFootwork',
    'Presence of Mind': 'presenceOfMind',
    'Legend: Alacrity': 'legendAlacrity',
    'Legend: Haste': 'legendHaste',
    'Legend: Bloodline': 'legendBloodline',
    'Coup de Grace': 'coupDeGrace',
    'Cut Down': 'cutDown',
    'Last Stand': 'lastStand',
    'Absorb Life': 'absorbLife',
    'Dark Harvest': 'darkHarvest',
    'Hail of Blades': 'hailOfBlades',
    'Cheap Shot': 'cheapShot',
    'Taste of Blood': 'tasteOfBlood',
    'Sudden Impact': 'suddenImpact',
    'Zombie Ward': 'zombieWard',
    'Ghost Poro': 'ghostPoro',
    'Eyeball Collection': 'eyeballCollection',
    'Treasure Hunter': 'treasureHunter',
    'Ingenious Hunter': 'ingeniousHunter',
    'Relentless Hunter': 'relentlessHunter',
    'Ultimate Hunter': 'ultimateHunter',
    'Grasp of the Undying': 'graspOfTheUndying',
    'Font of Life': 'fontOfLife',
    'Shield Bash': 'shieldBash',
    'Bone Plating': 'bonePlating',
    'Second Wind': 'secondWind',
    'First Strike': 'firstStrike',
    'Magical Footwear': 'magicalFootwear',
    'Biscuit Delivery': 'biscuitDelivery',
    'Cosmic Insight': 'cosmicInsight',
    'Approach Velocity': 'approachVelocity',
    'Time Warp Tonic': 'timeWarpTonic',
    'Unsealed Spellbook': 'unsealedSpellbook',
    'Hextech Flashtraption': 'hextechFlashtraption',
    'Triple Tonic': 'tripleTonic',
    'Jack of All Trades': 'jackOfAllTrades',
    'Glacial Augment': 'glacialAugment',
    'Deep Ward': 'deepWard',
    'Sixth Sense': 'sixthSense',
    'Grisly Mementos': 'grislyMementos',
  };

  // Check if we have a direct mapping
  if (runeNameMapping[runeName]) {
    return runeNameMapping[runeName];
  }

  // Fallback: convert to camelCase automatically
  return runeName
    .split(' ')
    .map((word, index) => {
      if (index === 0) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join('');
}

interface UniversalImageRequest {
  sourceType: string;
  imageState?: string;
  sourceName?: string;
  championName?: string;
  abilityKey?: string;
  petName?: string; // For pet damage sources
}

/**
 * Universal image fetcher function that standardizes image fetching across all event renderers
 */
export function getUniversalSourceIcon(
  request: UniversalImageRequest,
  gameWideAssets: any,
  championsAssets: any
): React.ReactNode {
  const { sourceType, imageState, sourceName, championName, abilityKey, petName } = request;
  const { itemImages, runeIcons, kircheisShardIcon } = gameWideAssets;

  // Handle Pet type - pets have a special golden border
  if (sourceType === 'Pet' && petName) {
    const petImageUrl = getPetImageUrl(petName);
    if (petImageUrl) {
      return (
        <img
          src={petImageUrl}
          alt={petName}
          className="source-icon source-icon-pet"
          title={petName}
        />
      );
    }
    // Fallback: try to get from championImages using imageState
    if (championName && imageState) {
      const champMap = championImages[championName];
      if (champMap) {
        const petIconUrl = champMap[imageState.toLowerCase()] || champMap[petName.toLowerCase()];
        if (petIconUrl) {
          return (
            <img
              src={petIconUrl}
              alt={petName}
              className="source-icon source-icon-pet"
              title={petName}
            />
          );
        }
      }
    }
    // Final fallback: text
    return <span className="source-name source-name-pet">{petName}</span>;
  }

  // Handle Ability and Passive types using championImages (up-to-date)
  if ((sourceType === 'Ability' || sourceType === 'Passive') && championName) {
    const champMap = championImages[championName];
    if (champMap) {
      let finalIconUrl: string | undefined;

      // For Passive sources, always try to get the passive image first
      if (sourceType === 'Passive') {
        // Try championImages with 'passive' key first
        finalIconUrl = champMap['passive'];
        
        // Fallback to championsAssets for passives
        if (!finalIconUrl && championsAssets[championName]?.passiveImageUrl) {
          finalIconUrl = championsAssets[championName].passiveImageUrl;
        }
      } else if (sourceType === 'Ability') {
        // For Ability sources, try imageState first
        if (imageState && imageState !== 'default') {
          finalIconUrl = champMap[imageState.toLowerCase()];
        }
        
        // Try championImages with abilityKey if no imageState result
        if (!finalIconUrl && abilityKey) {
          finalIconUrl = champMap[abilityKey.toLowerCase()];
        }

        // Fallback to championsAssets for abilities
        if (!finalIconUrl && championsAssets[championName]?.spellImageUrls && imageState) {
          finalIconUrl = championsAssets[championName].spellImageUrls[imageState.toUpperCase()];
        }
      }

      if (finalIconUrl) {
        return (
          <img
            src={finalIconUrl}
            alt={sourceName || `${championName} ${sourceType}`}
            className="source-icon"
          />
        );
      }
    }
  }

  // Handle Item type
  if (sourceType === 'Item') {
    if (imageState === 'Energized') {
      return (
        <img
          src={kircheisShardIcon}
          alt={sourceName || imageState}
          className="source-icon"
          title="Energized Passives"
        />
      );
    }
    if (imageState && imageState !== 'default') {
      const itemId = itemNameToId[imageState as keyof typeof itemNameToId];
      if (itemId && itemImages[itemId]) {
        return (
          <img
            src={itemImages[itemId]}
            alt={sourceName || imageState}
            className="source-icon"
            title={sourceName || imageState}
          />
        );
      }
    }
  }

  // Handle Rune type
  if (sourceType === 'Rune' && sourceName) {
    const runeKey = convertRuneNameToKey(sourceName);
    const runeImageUrl = runeIcons[runeKey];
    if (runeImageUrl) {
      return (
        <img
          src={runeImageUrl}
          alt={sourceName}
          className="source-icon"
        />
      );
    }
  }

  // Handle Mark Detonation type (similar to DamageEvent)
  if (sourceType === 'Mark Detonation' && sourceName) {
    const debuffImageUrl = debuffImages[sourceName];
    if (debuffImageUrl) {
      return (
        <img
          src={debuffImageUrl}
          alt={sourceName}
          className="source-icon-mark-detonation"
        />
      );
    }
  }

  // Handle Auto Attack type
  if (sourceType === 'Auto Attack' && championName) {
    const autoAttackImageUrl = championsAssets[championName]?.autoAttackImageUrl;
    if (autoAttackImageUrl) {
      return (
        <img
          src={autoAttackImageUrl}
          alt="Auto Attack"
          className="source-icon"
        />
      );
    }
  }

  // Handle Life Steal type
  if (sourceType === 'Life Steal') {
    return <div className="stat-icon life-steal-icon" />;
  }

  // Fallback to source name text
  return <span className="source-name">{sourceName || 'Unknown Source'}</span>;
}

// Mapping from stat variable names to display names
const statDisplayNames: Record<string, string> = {
  'attackSpeed': 'Attack Speed',
  'attackDamage': 'Attack Damage',
  'abilityPower': 'Ability Power',
  'armor': 'Armor',
  'magicResistance': 'Magic Resistance',
  'movementSpeed': 'Movement Speed',
  'movementSpeedPercent': 'Movement Speed (%)',
  'resourceRegen': 'Resource Regeneration',
  'armorPenetrationPercent': 'Armor Penetration (%)',
  'lethality': 'Lethality',
  'magicPenetration': 'Magic Penetration',
  'magicPenetrationPercent': 'Magic Penetration (%)',
  'health': 'Health',
  'omnivamp': 'Omnivamp',
  'healEmpowered': 'Heal Empowered',
  'healShieldPower': 'Heal & Shield Power',
  'healAndShieldPower': 'Heal & Shield Power',
  'shield': 'Shield',
  'magicShield': 'Magic Shield',
  'physicalShield': 'Physical Shield',
  'critChance': 'Critical Strike Chance',
  'criticalStrikeChance': 'Critical Strike Chance',
  'manaRegen': 'Mana Regeneration',
  'manaRegeneration': 'Mana Regeneration',
  'baseManaRegeneration': 'Base Mana Regen',
  'baseManaRegen': 'Base Mana Regen',
  'baseManaRegenerationPercent': 'Base Mana Regen (%)',
  'baseManaRegenPercent': 'Base Mana Regen (%)',
  'healthRegen': 'Health Regeneration',
  'baseHealthRegeneration': 'Base Health Regen',
  'baseHealthRegen': 'Base Health Regen',
  'baseHealthRegenPercent': 'Base Health Regen (%)',
  'baseHealthRegenerationPercent': 'Base Health Regen (%)',
  'lifeSteal': 'Life Steal',
  'spellVamp': 'Spell Vamp',
  'physicalVamp': 'Physical Vamp',
  'tenacity': 'Tenacity',
  'abilityHaste': 'Ability Haste',
  'attackRange': 'Attack Range',
  'damageReduction': 'Damage Reduction',
  'physicalDamageReduction': 'Physical Damage Reduction',
  'magicDamageReduction': 'Magic Damage Reduction',
  'gold': 'Gold',
  'goldPer10': 'Gold per 10s',
};

const StatChangeEvent: React.FC<BaseRendererProps> = ({ log }) => {
  const { gameWideAssets, championsAssets, } = useContext(GameAssetsContext);
  const { itemImages, runeIcons, shieldIcon, healEmpoweredIcon, kircheisShardIcon, damageReductionIcon } = gameWideAssets;

  const details = log.details;
  const imageState = log.imageState;
  const {
    championName,
    statName,
    changeAmount,
    totalAmount,
    changeType,
    modifierType,
    source,
    statChangeOrigin,
  } = details;

  // Determine the stat icon based on statName
  let statIcon = null;
  switch (statName) {
    case 'attackSpeed':
      statIcon = <div className="stat-icon attack-speed-icon"></div>;
      break;
    case 'attackDamage':
      statIcon = <div className="stat-icon attack-damage-icon"></div>;
      break;
    case 'abilityPower':
      statIcon = <div className="stat-icon ability-power-icon"></div>;
      break;
    case 'armor':
      statIcon = <div className="stat-icon armor-icon"></div>;
      break;
    case 'magicResistance':
      statIcon = <div className="stat-icon magic-resist-icon"></div>;
      break;
    case 'movementSpeed':
      statIcon = <div className="stat-icon movement-speed-icon"></div>;
      break;
    case 'movementSpeedPercent':
      statIcon = (
        <div className="stat-icon-with-indicator">
          <div className="stat-icon movement-speed-icon"></div>
          <span className="stat-percent-indicator">%</span>
        </div>
      );
      break;
    case 'resourceRegen':
      statIcon = <div className="stat-icon resource-regen-icon"></div>;
      break;
    case 'armorPenetrationPercent':
    case 'lethality':
      statIcon = <div className="stat-icon armor-penetration-icon"></div>;
      break;
    case 'magicPenetration':
    case 'magicPenetrationPercent':
      statIcon = <div className="stat-icon magic-penetration-icon"></div>;
      break;
    case 'health':
      statIcon = <div className="stat-icon health-icon"></div>;
      break;
    case 'omnivamp':
        statIcon = <div className="stat-icon omnivamp-icon"></div>;
        break;
    case 'gold':
    case 'goldPer10':
      statIcon = <div className="stat-icon gold-icon"></div>;
      break;
    case 'healShieldPower':
    case 'healAndShieldPower':
      statIcon = <div className="stat-icon heal-shield-power-icon"></div>;
      break;
    case 'healthRegen':
    case 'baseHealthRegeneration':
    case 'baseHealthRegen':
    case 'baseHealthRegenPercent':
    case 'baseHealthRegenerationPercent':
      statIcon = <div className="stat-icon health-regen-icon"></div>;
      break;
    case 'manaRegen':
    case 'manaRegeneration':
    case 'baseManaRegeneration':
    case 'baseManaRegen':
    case 'baseManaRegenerationPercent':
    case 'baseManaRegenPercent':
      statIcon = <div className="stat-icon resource-regen-icon"></div>;
      break;
    case 'abilityHaste':
      statIcon = <div className="stat-icon ability-haste-icon"></div>;
      break;
    case 'basicAbilityHaste':
      statIcon = <div className="stat-icon ability-haste-icon"></div>;
      break;
    case 'ultimateHaste':
      statIcon = <div className="stat-icon ability-haste-icon"></div>;
      break;
    case 'lifeSteal':
      statIcon = <div className="stat-icon life-steal-icon"></div>;
      break;
    case 'mana':
      statIcon = <div className="stat-icon resource-icon"></div>;
      break;
    case 'damageReduction':
      statIcon = (
        <div className="damage-mitigation-icon-container">
          <img
            src={damageReductionIcon}
            alt="Damage Reduction Icon"
            className="damage-mitigation-icon"
          />
          <span className="damage-reduction-arrow">↓</span>
        </div>
      );
      break;
    case 'physicalDamageReduction':
      statIcon = (
        <div className="damage-mitigation-icon-container">
          <img
            src={damageReductionIcon}
            alt="Physical Damage Reduction Icon"
            className="damage-mitigation-icon physical-damage-reduction"
          />
          <span className="damage-reduction-arrow">↓</span>
        </div>
      );
      break;
    case 'magicDamageReduction':
      statIcon = (
        <div className="damage-mitigation-icon-container">
          <img
            src={damageReductionIcon}
            alt="Magic Damage Reduction Icon"
            className="damage-mitigation-icon magic-damage-reduction"
          />
          <span className="damage-reduction-arrow">↓</span>
        </div>
      );
      break;
    case 'healEmpowered':
        statIcon = statIcon = (
          <div className="shield-icon-container">
            <img
              src={healEmpoweredIcon}
              alt={`${statName} Icon`}
              className={`shield-icon-image ${statName}`}
            />
          </div>
        );
      break;
    case 'shield':
    case 'magicShield':
    case 'physicalShield':
      statIcon = (
        <div className="shield-icon-container">
          <img
            src={shieldIcon}
            alt={`${statName} Icon`}
            className={`shield-icon-image ${statName}`}
          />
        </div>
      );
      break;

      
    case 'critChance':
    case 'criticalStrikeChance':
      statIcon = <div className="stat-icon crit-chance-icon"></div>;
      break;
    // Add other stat cases as needed
    default:
      statIcon = <div className="stat-icon default-stat-icon"></div>;
  }

  // Use the new universal image fetcher
  const sourceIcon = getUniversalSourceIcon(
    {
      sourceType: details.sourceType || statChangeOrigin || 'Unknown',
      imageState: imageState,
      sourceName: source,
      championName: championName,
      abilityKey: imageState, // For abilities, imageState often contains the ability key
    },
    gameWideAssets,
    championsAssets
  );

  // Determine the sign and display amount
  // Special handling for damage reduction - always show as negative in tooltip
  const isDamageReduction = statName === 'damageReduction' || statName === 'physicalDamageReduction' || statName === 'magicDamageReduction';
  const sign = changeType === 'increase' ? '+' : '-';
  
  // Check if we should show decimal values
  const showDecimal = details.showDecimalValue === true;
  const displayIncrementalAmount = showDecimal ? 
    Math.abs(changeAmount).toString() : 
    Math.abs(changeAmount).toFixed(0);
  const displayTotalAmount = showDecimal ? 
    Math.abs(totalAmount).toString() : 
    Math.abs(totalAmount).toFixed(0);
  const unit = modifierType === 'percent' ? '%' : '';

  // Decide what to display based on whether this is a stacking source, resource usage, and changeAmount
  let amountDisplay = '';
  let totalDisplay = '';
  
  // Check if this is from a stacking source (like Conqueror, Ezreal passive, etc.)
  const isStackingSource = details.isStackingSource;
  
  // Check if this is a resource usage event (ability cost)
  const isResourceUsage = modifierType === 'ResourceUsage';
  
  if (isResourceUsage) {
    // For resource usage, show the amount used and percentage
    const percentageUsed = details.percentageChange ? (details.percentageChange * 100).toFixed(1) : '0';
    amountDisplay = `${(changeAmount).toFixed(0)}`;
    totalDisplay = `(${percentageUsed}%)`;
  } else if (isStackingSource) {
    if (changeAmount !== 0) {
      // Show total amount on first line, incremental in brackets on second line
      const stackingDisplaySign = isDamageReduction ? '-' : '+';
      amountDisplay = `${stackingDisplaySign}${displayTotalAmount}${unit}`;
      totalDisplay = `(${stackingDisplaySign}${displayIncrementalAmount}${unit})`;
    } else {
      // Show total with +0% underneath when no incremental change
      const stackingDisplaySign = isDamageReduction ? '-' : '+';
      amountDisplay = `${stackingDisplaySign}${displayTotalAmount}${unit}`;
      totalDisplay = `(${stackingDisplaySign}0${unit})`;
    }
  } else {
    if (changeAmount !== 0) {
      // Show only incremental amount for other stats
      // For damage reduction, always show as negative regardless of changeType
      const displaySign = isDamageReduction ? '-' : sign;
      amountDisplay = `${displaySign}${displayIncrementalAmount}${unit}`;
    } else {
      // Optionally handle zero change amount
      amountDisplay = `${statName} unchanged`;
    }
  }

  // Get the display name for the stat
  const statDisplayName = statDisplayNames[statName] || statName;

  // Create detailed tooltip based on what values we're actually showing
  let tooltipText = '';
  
  if (isResourceUsage) {
    // For resource usage events, show "Ability Cost" in tooltip
    const percentageUsed = details.percentageChange ? (details.percentageChange * 100).toFixed(1) : '0';
    tooltipText = `Ability Cost: ${Math.abs(changeAmount).toFixed(0)} (${percentageUsed}% of total resources)`;
  } else if (isStackingSource) {
    if (changeAmount !== 0) {
      // Stacking sources show total first, then incremental in brackets
      const stackingSign = isDamageReduction ? '-' : '+';
      tooltipText = `Total ${statDisplayName} Change: ${stackingSign}${displayTotalAmount}${unit}\nAdditional ${statDisplayName}: ${stackingSign}${displayIncrementalAmount}${unit}`;
    } else {
      // Zero incremental change - show total and indicate no additional change
      const stackingSign = isDamageReduction ? '-' : '+';
      tooltipText = `Total ${statDisplayName} Change: ${stackingSign}${displayTotalAmount}${unit}\nNo additional change this instance`;
    }
  } else if (changeAmount !== 0) {
    // Other stats only show incremental, so only show incremental in tooltip
    // For damage reduction, always show as negative regardless of changeType
    const tooltipSign = isDamageReduction ? '-' : sign;
    tooltipText = `${statDisplayName}: ${tooltipSign}${displayIncrementalAmount}${unit}`;
  } else {
    // Zero change amount case
    const zeroSign = isDamageReduction ? '-' : '';
    tooltipText = `Total ${statDisplayName}: ${zeroSign}${displayTotalAmount}${unit}`;
  }

  return (
    <div className="stat-change-event" title={tooltipText}>
      <div className="stat-change-amount-wrapper">
        {statIcon}
        <div className="stat-change-amounts">
          <span className="stat-change-amount">{amountDisplay}</span>
          {totalDisplay && <span className="stat-change-total">{totalDisplay}</span>}
        </div>
      </div>
      {sourceIcon && (
        <div className="stat-change-source-wrapper">
          <FontAwesomeIcon icon={faTurnUp} className="fa-turn-up" />
          {sourceIcon}
        </div>
      )}
    </div>
  );
};

export default StatChangeEvent;
